import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useConfig } from "@/lib/config";
import { ArrowRight, Check, ExternalLink, ClipboardList, FileText, Users } from "lucide-react";

const PLANS_FALLBACK_URL = "https://mlsapp.smartnote2self.com/plans";

const BUYER_STEPS = [
  {
    icon: ClipboardList,
    title: "Run the scan for your client",
    body: "Kick off a guided Biz Resilience Scan on their behalf — no jargon, no heavy tooling. Answer alongside the client or send them the questions.",
  },
  {
    icon: FileText,
    title: "Hand over a readiness brief",
    body: "Walk away with a clean, plain-English resilience readiness brief — dependencies, single points of failure, and prioritised next steps, ready to present.",
  },
  {
    icon: Users,
    title: "Look like the trusted advisor",
    body: "Your firm's name prints on the brief cover (“Prepared by …”). Deliver board-ready output in an afternoon, not a six-week engagement.",
  },
];

function Tier({ name, tagline, features, highlight, plansUrl }) {
  const slug = name.toLowerCase().replace(/\s+/g, "-");
  return (
    <div
      data-testid={`advisor-tier-${slug}`}
      className={`relative flex flex-col rounded-2xl border p-7 ${
        highlight
          ? "border-teal-500 dark:border-teal-500 bg-white dark:bg-slate-900 shadow-lg shadow-teal-500/10"
          : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
      }`}
    >
      {highlight && (
        <span className="absolute -top-3 left-7 inline-flex items-center h-6 px-3 rounded-full bg-teal-600 text-white text-[11px] font-semibold tracking-wide">
          Most popular
        </span>
      )}
      <p className="text-[11px] uppercase tracking-[0.16em] text-teal-700 dark:text-teal-400">{name}</p>
      <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{tagline}</p>

      <ul className="mt-5 space-y-2.5 text-sm text-slate-700 dark:text-slate-200 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Check className="h-4 w-4 text-teal-600 dark:text-teal-400 mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <a
        href={plansUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`advisor-tier-plans-cta-${slug}`}
        className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors w-full justify-center"
      >
        View plans &amp; pricing <ExternalLink className="h-4 w-4" />
      </a>
    </div>
  );
}

export default function Advisors() {
  const { config } = useConfig();
  const plansUrl = config?.advisor_toolkit_url || PLANS_FALLBACK_URL;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <Header />

      {/* HERO — advisor buyer voice */}
      <section className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800">
        <div className="absolute inset-0 hero-grid pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 pt-20 pb-16">
          <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400" data-testid="advisor-eyebrow">
            For IT advisors &amp; fractional CxOs
          </p>
          <h1
            data-testid="advisor-headline"
            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 leading-[1.12]"
          >
            Deliver a resilience readiness brief to your clients — in an afternoon.
          </h1>
          <p className="mt-6 text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl">
            You run the scan on behalf of a client and hand over a clean, plain-English brief that shows
            where their operations are exposed and what to fix first. No jargon, no six-week engagement,
            no continuity-planning overhead — just decisions your client can act on.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              to="/mode?advisor=1"
              data-testid="advisor-hero-scan-cta"
              className="group inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors"
            >
              Start a free client scan
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <a
              href="#advisor-pricing"
              data-testid="advisor-hero-pricing-link"
              className="inline-flex items-center h-11 px-5 rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
            >
              See the toolkit
            </a>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS FOR ADVISORS */}
      <section className="border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight max-w-2xl">
            Run it for the client. Hand over the brief.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {BUYER_STEPS.map((s) => (
              <div
                key={s.title}
                data-testid={`advisor-step-${s.title.split(" ")[0].toLowerCase()}`}
                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6"
              >
                <s.icon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                <h3 className="mt-3 font-display text-base font-semibold text-slate-900 dark:text-slate-50">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLKIT — cards hand off to the sister plans page for live pricing */}
      <section id="advisor-pricing" className="border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Advisor toolkit</p>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
            Tools built for client delivery
          </h2>
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 max-w-2xl">
            Choose the toolkit that fits how you work. Live monthly &amp; annual pricing — and checkout —
            is on the plans page.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Tier
              name="Advisor Toolkit"
              highlight
              plansUrl={plansUrl}
              tagline="Both scans, for advisors who deliver resilience and licensing reviews to clients."
              features={[
                "Biz Resilience Scan — full guided assessment",
                "MS License Scan — Microsoft licensing review for client reports",
                "Client-branded readiness briefs (“Prepared by your firm”)",
                "Unlimited client scans",
                "Priority support",
              ]}
            />
            <Tier
              name="Single tool"
              plansUrl={plansUrl}
              tagline="Resilience only — the essential toolkit for continuity-focused advisors."
              features={[
                "Biz Resilience Scan — full guided assessment",
                "Client-branded readiness briefs",
                "Unlimited client scans",
                "Email support",
              ]}
            />
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={plansUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="advisor-view-plans-cta"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-teal-600 dark:border-teal-500 text-teal-800 dark:text-teal-200 text-sm font-medium hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors"
            >
              View all plans &amp; pricing <ExternalLink className="h-4 w-4" />
            </a>
            <Link
              to="/mode?advisor=1"
              data-testid="advisor-pricing-free-scan"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md text-slate-700 dark:text-slate-200 text-sm font-medium hover:text-slate-900 dark:hover:text-white transition-colors"
            >
              or start a free client scan first <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
